import { useSyncExternalStore } from 'react';

/*
 * Chrome's notification permission, as one shared value.
 *
 * Three places ask about it: the bell (which registers for push once it is granted), the banner
 * in the app shell, and the Chrome notifications row on /notifications/my-alerts. Each used to be
 * free to read `Notification.permission` into its own state, which goes stale the moment a
 * decision is made somewhere else: grant from the banner and the alerts page still offered
 * "Turn on", and the bell did not start registering until a reload. So every reader subscribes to
 * this store, and every request goes through `requestPushPermission`, which tells all of them.
 *
 * The browser is the source of truth, not this module: the snapshot is read live from
 * `Notification.permission` every time, so there is no copy to drift. The store's only job is to
 * say "read it again" -- after a request, when Chrome reports a change through the Permissions
 * API (the user flipped it in site settings), and when the window regains focus.
 */

export type PushPermission = 'unsupported' | 'default' | 'granted' | 'denied';

/**
 * Push needs both halves: the Notification API for the permission and a service worker to
 * receive while the console is in the background. Safari in a plain tab and some embedded
 * browsers have the first without the second; they are "unsupported", not "default", so nothing
 * offers them a button that cannot work.
 */
export function readPushPermission(): PushPermission {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'unsupported';
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return 'unsupported';
  const value = window.Notification.permission;
  return value === 'granted' || value === 'denied' ? value : 'default';
}

const listeners = new Set<() => void>();
let detachBrowserSignals: (() => void) | null = null;

function notify() {
  listeners.forEach((listener) => listener());
}

/** Wire the browser's own change signals while anyone is listening, and only then. */
function attachBrowserSignals(): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('focus', notify);
  let status: PermissionStatus | null = null;
  let detached = false;
  try {
    navigator.permissions
      ?.query({ name: 'notifications' as PermissionName })
      .then((s) => {
        if (detached) return;
        status = s;
        status.addEventListener('change', notify);
      })
      .catch(() => {});
  } catch {
    // No Permissions API: focus alone still catches a change made in site settings.
  }
  return () => {
    detached = true;
    window.removeEventListener('focus', notify);
    status?.removeEventListener('change', notify);
  };
}

export function subscribePushPermission(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) detachBrowserSignals = attachBrowserSignals();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && detachBrowserSignals) {
      detachBrowserSignals();
      detachBrowserSignals = null;
    }
  };
}

/**
 * Ask Chrome. Only ever called from a click: Chrome ignores (and penalises) a prompt that no user
 * gesture asked for. Never throws; an API failure reads as no change.
 */
export async function requestPushPermission(): Promise<PushPermission> {
  if (readPushPermission() === 'unsupported') return 'unsupported';
  try {
    await window.Notification.requestPermission();
  } catch {
    // Some browsers only implement the callback form; the live read below is the answer anyway.
  }
  notify();
  return readPushPermission();
}

/** Re-read and tell every subscriber. Tests, and anything that knows the value moved. */
export function refreshPushPermission(): void {
  notify();
}

export function usePushPermission(): PushPermission {
  return useSyncExternalStore(subscribePushPermission, readPushPermission, () => 'unsupported');
}
